import {
  workflowProgressIconSelector,
  workflowIconOffsets,
} from '../../constants'

import type { WorkflowProgressData } from '../../types'
import preparedIcon from '@images/icon-prepared.svg?inline'
import reviewedIcon from '@app/shared/assets/icons/svg/icon-reviewed.svg?inline'
import reviewedNotByYouIcon from '@images/icon-reviewed-not-by-you.svg?inline'
import { getReviewCounterIconInline } from '@app/shared/assets/icons/review-counter-icons'

import pipe from 'lodash/fp/pipe'

type IconData = {
  wrapper: HTMLElement
  specificClasses?: Array<string>
  offset?: number
  apiObj?: WorkflowProgressData
  tooltipTitleKey?: string
}

type ImageIconData = {
  cell: HTMLElement
  apiObj?: WorkflowProgressData
  tooltip?: string
  image: string
}
const absoluteTopCenterClasses = [
  'tw-absolute',
  'tw-top-2',
  'tw-left-1/2',
  'tw-transform',
  '-tw-translate-x-1/2',
]

const clearIcons = (wrapper: HTMLElement): void => {
  wrapper
    .querySelectorAll(`.${workflowProgressIconSelector}`)
    .forEach((icon) => icon.remove())
}

const ensureIconWrapper = (cell: HTMLElement): HTMLElement => {
  const wrapper = cell.querySelector('.icons-wrapper') as HTMLElement
  if (wrapper) {
    return wrapper
  }

  const newWrapper = document.createElement('div')
  newWrapper.classList.add(
    'tw-absolute',
    'tw-left-0',
    'tw-top-2',
    'icons-wrapper'
  )
  cell.append(newWrapper)

  return newWrapper
}

const renderImageIcon = ({
  cell,
  tooltip,
  image,
}: ImageIconData): HTMLElement => {
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(image, 'image/svg+xml')
  const svgElement = svgDoc.documentElement

  if (tooltip) {
    svgElement.setAttribute('title', tooltip)
    svgElement.dataset.toggle = 'tooltip'
    svgElement.dataset.html = 'true'
    svgElement.dataset.textLeft = 'true'
  }

  svgElement.classList.add('tw-w-4', 'tw-h-4', ...absoluteTopCenterClasses)

  cell.append(svgElement)
  return svgElement
}

const renderFontIcon = ({
  wrapper,
  specificClasses = [],
  tooltipTitleKey,
  offset = 0,
}: IconData): HTMLElement => {
  const icon = document.createElement('i')
  const classes = ['tw-absolute', 'tw-left-0']

  icon.classList.add(...classes, ...specificClasses.filter(Boolean))
  icon.style.marginLeft = `${offset}px`

  wrapper.append(icon)

  // this should only be true for an icon, not spinner
  if (tooltipTitleKey) {
    icon.title = window.I18n.t(tooltipTitleKey)
    icon.dataset.toggle = 'tooltip'
  }

  return icon
}

const addOffset =
  (offset: number) =>
  (data: IconData): IconData => ({
    ...data,
    offset,
  })

const addSpecificClasses =
  (classes: Array<string>) =>
  (data: IconData): IconData => ({
    ...data,
    specificClasses: classes,
  })

const addTooltipTitle =
  (tooltipTitleKey: string) =>
  (data: IconData): IconData => {
    return {
      wrapper: data.wrapper,
      specificClasses: data.specificClasses,
      tooltipTitleKey: tooltipTitleKey,
      offset: data.offset,
    }
  }

export const addReconciledClassesAndTooltipTitle = (
  data: IconData
): IconData => {
  const { apiObj } = data

  if (!apiObj) {
    return data
  }

  let classes: string[]
  let tooltipTitleKey

  if (
    apiObj.object_type === 'reconciliation_text' &&
    apiObj.reconciliation_not_necessary
  ) {
    classes = ['i-no-reconciliation-needed']
    tooltipTitleKey = 'tooltips.reconciliation.icons.data_entered'

    if (!apiObj.has_text_properties) {
      classes.push('no-text-properties')
      tooltipTitleKey = 'tooltips.reconciliation.icons.no_data_entered'
    }
  } else if (apiObj.reconciled) {
    classes = ['i-reconciled', 'sf-c-success-light-1']
    tooltipTitleKey = 'tooltips.reconciliation.icons.complete'
  } else {
    classes = ['i-unreconciled', 'sf-c-danger-light-1']
    tooltipTitleKey = 'tooltips.reconciliation.icons.action_needed'
  }

  return {
    ...data,
    specificClasses: [...classes, workflowProgressIconSelector],
    tooltipTitleKey: tooltipTitleKey,
  }
}

const addTooltip =
  (tooltip?: string) =>
  (icon: HTMLElement): HTMLElement => {
    if (!tooltip) {
      return icon
    }

    icon.dataset.toggle = 'tooltip'
    icon.dataset.html = 'true'
    icon.dataset.textLeft = 'true'
    icon.title = tooltip
    return icon
  }

const renderReconciled = pipe(
  addOffset(workflowIconOffsets.RECONCILED),
  addReconciledClassesAndTooltipTitle,
  renderFontIcon
)

const renderNotes = pipe(
  addOffset(workflowIconOffsets.NOTES),
  addSpecificClasses(['i-comment-solid', workflowProgressIconSelector]),
  addTooltipTitle('tooltips.reconciliation.icons.note_added'),
  renderFontIcon
)

const renderTodos = pipe(
  addOffset(workflowIconOffsets.TODOS),
  addSpecificClasses([
    'i-bolt',
    'sf-c-success-light-1',
    workflowProgressIconSelector,
  ]),
  addTooltipTitle('tooltips.reconciliation.icons.todo_added'),
  renderFontIcon
)

const renderChecks = pipe(
  addOffset(workflowIconOffsets.CHECKS),
  addSpecificClasses([
    'i-square',
    'sf-c-primary',
    workflowProgressIconSelector,
  ]),
  addTooltipTitle('tooltips.reconciliation.icons.checks_present'),
  renderFontIcon
)

export const addSpinner = (cell?: HTMLElement): void => {
  if (!cell) {
    return
  }
  const wrapper = ensureIconWrapper(cell)
  const existingSpinner = wrapper.querySelector('.sf-spinner') as HTMLElement

  if (existingSpinner) {
    return
  }

  clearIcons(wrapper)

  pipe(
    addOffset(workflowIconOffsets.RECONCILED),
    addSpecificClasses(['sf-spinner', 'sf-spinner-primary']),
    renderFontIcon
  )({ wrapper })
}

export const clearSpinner = (cell?: HTMLElement): void => {
  if (!cell) {
    return
  }
  const spinner = cell.querySelector('.sf-spinner')
  spinner?.remove()
}

export const renderProposalIcon = ({
  cell,
  tooltip,
}: {
  cell?: HTMLElement
  tooltip?: string
} = {}): void => {
  if (!cell) {
    return
  }
  const wrapper = ensureIconWrapper(cell)
  pipe(
    addOffset(workflowIconOffsets.PROPOSAL),
    addSpecificClasses(['fa', 'fa-edit', 'sf-c-text-darker']),
    renderFontIcon,
    addTooltip(tooltip)
  )({ wrapper })
}

export const renderPreparedIcon = ({
  cell,
  tooltip,
}: {
  cell?: HTMLElement
  tooltip?: string
} = {}): HTMLElement | undefined => {
  if (!cell) {
    return
  }

  return renderImageIcon({
    cell,
    tooltip,
    image: preparedIcon,
  })
}

export const renderPreviouslyReviewedIcon = ({
  cell,
  tooltip,
}: {
  cell?: HTMLElement
  tooltip?: string
} = {}): HTMLElement | undefined => {
  if (!cell) {
    return
  }
  const elem = pipe(
    addSpecificClasses([
      'fa-solid',
      'fa-circle-exclamation',
      'tw-text-yellow-600',
      ...absoluteTopCenterClasses,
    ]),
    renderFontIcon,
    addTooltip(tooltip)
  )({ wrapper: cell })

  elem.dataset.testid = 'review-icon-outdated'

  return elem
}

export const renderReviewIcon = ({
  cell,
  tooltip,
  reviewedByCurrentUser,
  count,
}: {
  cell?: HTMLElement
  tooltip?: string
  reviewedByCurrentUser?: boolean
  count?: number
} = {}): HTMLElement | undefined => {
  if (!cell) {
    return
  }

  if (!count) {
    const image = reviewedByCurrentUser ? reviewedIcon : reviewedNotByYouIcon

    return renderImageIcon({
      cell,
      tooltip,
      image,
    })
  }

  const image = getReviewCounterIconInline(!!reviewedByCurrentUser, count)

  const svgElement = renderImageIcon({
    cell,
    tooltip,
    image,
  })

  svgElement.dataset.testid = reviewedByCurrentUser
    ? 'review-icon-current-user'
    : 'review-icon-other-user'

  return svgElement
}

export const renderMissingReconciliationIcon = (cell: HTMLElement): void => {
  const iconsWrapper = ensureIconWrapper(cell)

  renderFontIcon({
    wrapper: iconsWrapper,
    specificClasses: [
      'i-no-reconciliation-needed',
      'no-text-properties',
      'workflow-progress-icon',
    ],
    tooltipTitleKey: 'tooltips.reconciliation.icons.starred_missing_data',
    offset: 16,
  })
}

export const renderWorkflowProgressIcons = ({
  cell,
  apiObj,
}: {
  cell?: HTMLElement
  apiObj?: WorkflowProgressData
}): void => {
  if (!cell || !apiObj) {
    return
  }

  clearSpinner(cell)

  const wrapper = ensureIconWrapper(cell)
  const data = { wrapper, apiObj }

  clearIcons(wrapper)

  if (apiObj.starred) {
    // for starred RTs, always render the reconciled icon
    // for starred accounts, render it only if it has data (i.e. has an AE)
    if (
      apiObj.object_type === 'report_reconciliation_text' ||
      apiObj.has_data
    ) {
      renderReconciled(data)
    } else {
      // is account, but no data -> render the "no data" icon (square)
      renderMissingReconciliationIcon(cell)
    }
  }

  if (apiObj.has_notes) {
    renderNotes(data)
  }
  if (apiObj.has_todos) {
    renderTodos(data)
  }
  if (apiObj.has_checks) {
    renderChecks(data)
  }
}
